import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {inject, Injectable, Injector} from '@angular/core';
import {from, Observable, switchMap} from 'rxjs';
import {DateTime} from 'luxon';
import {AUTH_CONFIG, AuthService} from './auth.service';
import {CookieService} from 'ngx-cookie-service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private readonly injector = inject(Injector);
    private readonly config = inject(AUTH_CONFIG);
    private readonly cookieService = inject(CookieService);

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        // Convert promise to observable
        return from(this.authorizedRequest(request)).pipe(
            switchMap(authorizedRequest => next.handle(authorizedRequest)),
        );
    }

    private async authorizedRequest(request: HttpRequest<unknown>): Promise<HttpRequest<unknown>> {
        if (!this.config.interceptedUrls.some(url => request.url.includes(url))) {
            return request;
        }

        if (
            !this.cookieService.check('access_token') ||
            !this.cookieService.check('access_token_expires')
        ) {
            return await this.authorize();
        }
        const accessTokenExpiresString = this.cookieService.get('access_token_expires');
        const accessTokenExpires = DateTime.fromISO(accessTokenExpiresString);
        let accessToken: string | null = this.cookieService.get('access_token');
        if (accessTokenExpires <= DateTime.now()) {
            // Access token has expired. We need to refresh it.
            accessToken = await this.injector.get(AuthService).refreshAccessToken();
            if (accessToken == null) {
                return await this.authorize();
            }
        }

        return request.clone({
            headers: request.headers.set('Authorization', 'Bearer ' + accessToken),
        });
    }

    private async authorize(): Promise<never> {
        await this.injector.get(AuthService).authorize();
        return new Promise<never>(() => undefined);
    }
}
